import React, {useEffect, useState} from "react";

import {ProfileCard} from "./profile-card";
import Collapse from "@mui/material/Collapse/Collapse";

import CardActions from "@mui/material/CardActions/CardActions";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";

import useWidth from "../../hooks/use-width";

import {useTranslation} from "next-i18next";
import {LoadingButton} from "@mui/lab";
import {Box} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useSelector, useDispatch} from "../../store";
import {useAuth} from "../../hooks/use-auth";
import tripImagesById from "../../generated/trip-images-by-id.json"
import {getTrips} from "../../slices/trips-slice";
import {updateProfileInterested} from "../../slices/user-session-slice";
import NextLink from "next-translate-routes/link";
import {TripInterface} from "../../types/trip";
import googleBucketLoader from "../../lib/google-bucket-loader";
import tripDatabase from "../../generated/trip-database.json";
import Image from "next/legacy/image";


const colsForWidth = {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 3,
};

export const InterestedTripsCard = () => {


    const {t, i18n} = useTranslation();

    const {isUpdatingProfile, currentProfile: {uid, interested}} = useSelector((state) => state.userSession);

    const {trips, isLoaded} = useSelector((state) => state.trips);

    const [editing, setEditing] = useState(false);


    const dispatch = useDispatch();

    const auth = useAuth()

    const isAdmin = auth.isAdmin;

    const isAllowedToEdit = auth.user?.uid === uid || isAdmin;


    const width = useWidth();

    const cols = colsForWidth[width] || 1;


    useEffect(() => {

        if (!isLoaded) {
            dispatch(getTrips({language: i18n.language}));
        }

    }, [isLoaded, i18n.language]);

    const matchingTrips = trips.tripsArray.filter((trip) => {
        return !!interested && !!interested[trip.tripId];
    });


    const handleEditChange = (editing: boolean) => {
        setEditing(editing);
    };


    const handleDeleteClicked = (tripId: string) => {

        dispatch(updateProfileInterested({...interested, [tripId]: false}))
    };


    return (
        <ProfileCard
            title={t("interested-trips")}
            editing={editing}
            onEditingChanged={handleEditChange}
        >
            <Box mx={1}>
                {matchingTrips.length === 0 && !editing && (
                    <Box p={3}>
                        <Typography variant={"body1"} color={"textSecondary"}>
                            <SentimentDissatisfiedIcon style={{verticalAlign: "middle"}} fontSize={"large"}
                                                       htmlColor='#aaa'/>{" "}
                            {t("no-saved-trips-yet")}
                        </Typography>
                    </Box>
                )}


                <ImageList cols={cols}>
                    {matchingTrips.map((trip: TripInterface) => <NextLink
                            key={trip.tripId}
                            href={{
                                pathname: "/diving/[countrySlug]/[tripSlug]",
                                query: {
                                    countrySlug: trip.country.slug,
                                    tripSlug: trip.slug
                                }
                            }}
                            passHref
                            legacyBehavior

                        >
                            <ImageListItem
                                component={editing ? "div" : "a"}
                                key={trip.tripId}
                                cols={1}

                            >

                                <Image
                                    src={`/content/trips/${trip.tripId}/img/${tripImagesById[trip.tripId as keyof typeof tripImagesById][0]}`}
                                    width={1280} height={853}
                                    layout={"responsive"}
                                    loader={googleBucketLoader}
                                    placeholder={"blur"}
                                    blurDataURL={tripDatabase[trip.tripId as keyof typeof tripDatabase].firstImageBlurDataURL}
                                    sizes={"20vw"}
                                />

                                <ImageListItemBar
                                    title={trip.title}
                                    position='top'
                                    actionIcon={
                                        editing ? (
                                            <IconButton
                                                sx={{color: "white"}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClicked(trip.tripId);
                                                }}
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        ) : (
                                            <IconButton sx={{color: "white"}}>
                                                <FavoriteIcon/>
                                            </IconButton>
                                        )
                                    }
                                    actionPosition='right'
                                    style={{
                                        background:
                                            "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, " + "rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)"
                                    }}
                                />
                            </ImageListItem>
                        </NextLink>
                    )}
                </ImageList>
            </Box>

            {editing && (
                <Collapse in={editing}>
                    {matchingTrips.length === 0 && (
                        <Box
                            p={3}
                            dangerouslySetInnerHTML={{
                                __html: t("profile-interested-empty-edit"),
                            }}
                        />
                    )}

                    <CardActions style={{justifyContent: "flex-end"}}>

                        <LoadingButton
                            loading={isUpdatingProfile}
                            onClick={(e) => setEditing(false)}
                            size='medium'
                            variant='contained'
                            color='primary'
                            disabled={isUpdatingProfile}
                        >
                            {t("done")}
                        </LoadingButton>

                    </CardActions>
                </Collapse>
            )}
        </ProfileCard>
    );
}
